import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorText } from '../../shared/api/useErrorText';
import { useForm } from 'react-hook-form';
import { TextField, Button } from '../../shared/ui';
import { LanguageSwitcher } from '../../shared/LanguageSwitcher';
import { api } from '../../shared/api/client';

interface RequestForm {
  email: string;
}

interface ResetForm {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const requestForm = useForm<RequestForm>();
  const resetForm = useForm<ResetForm>();

  const requestCode = async (data: RequestForm) => {
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/password-reset/request', { email: data.email });
      setEmail(data.email);
      setStep('confirm');
    } catch (e: any) {
      setError(errorText(e, 'auth.resetRequestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (data: ResetForm) => {
    setError(null);
    if (data.newPassword !== data.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/password-reset/confirm', {
        email,
        code: data.code,
        newPassword: data.newPassword,
      });
      navigate('/login', { state: { message: t('auth.resetSuccess') } });
    } catch (e: any) {
      setError(errorText(e, 'auth.resetFailed'));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-surface font-body-md text-on-surface flex items-center justify-center min-h-screen">
      <main className="w-full max-w-md px-6 py-12">
        <div className="w-full max-w-md bg-surface-container-lowest rounded-3xl border border-hairline p-6 sm:p-10 shadow-soft relative overflow-hidden mx-auto">
          <div className="absolute -top-20 -right-20 w-48 h-48 bg-tertiary-fixed opacity-70 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-module-restaurant opacity-70 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute top-4 right-4 z-20">
            <LanguageSwitcher />
          </div>
          <div className="relative z-10">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-container-high mb-6 shadow-sm">
                <span className="material-symbols-outlined filled text-4xl text-primary">
                  lock_reset
                </span>
              </div>
              <h1 className="font-headline-xl text-headline-xl text-primary mb-2">
                {t('auth.forgotPasswordTitle')}
              </h1>
              <p className="font-body-md text-body-md text-on-surface-variant">
                {t('auth.forgotPasswordSubtitle')}
              </p>
            </div>

            {step === 'request' ? (
              <form className="space-y-6" onSubmit={requestForm.handleSubmit(requestCode)}>
                <div className="space-y-4">
                  <TextField
                    icon="mail"
                    placeholder={t('auth.universityEmail')}
                    type="email"
                    {...requestForm.register('email', { required: true })}
                  />
                </div>

                {error && <p className="text-error text-body-md text-center">{error}</p>}

                <Button type="submit" className="w-full" disabled={submitting}>
                  {t('auth.sendCode')}
                  <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                </Button>
              </form>
            ) : (
              <form className="space-y-6" onSubmit={resetForm.handleSubmit(resetPassword)}>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  {t('auth.resetCodeSent')}
                </p>
                <div className="space-y-4">
                  <TextField
                    icon="pin"
                    placeholder={t('auth.verificationCode')}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    {...resetForm.register('code', {
                      required: true,
                      minLength: 6,
                    })}
                  />
                  <TextField
                    icon="lock"
                    placeholder={t('auth.newPassword')}
                    type="password"
                    {...resetForm.register('newPassword', {
                      required: true,
                      minLength: 8,
                    })}
                  />
                  <TextField
                    icon="lock"
                    placeholder={t('auth.confirmPassword')}
                    type="password"
                    {...resetForm.register('confirmPassword', {
                      required: true,
                      minLength: 8,
                    })}
                  />
                </div>

                {error && <p className="text-error text-body-md text-center">{error}</p>}

                <div className="font-body-md text-body-md text-on-surface-variant">
                  {t('auth.passwordRequirements')}
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {t('auth.resetPassword')}
                  <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="font-body-md text-body-md text-on-surface-variant hover:text-on-surface"
              >
                {t('auth.signIn')}
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
